import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { BreadcrumbItem } from "../../../../components/common/breadcrumbs";
import { Heading, Text } from "../../../../components/common/typography";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { ProductCard } from "./product-card";
import { ProductsCompareBanner } from "./products-compare-banner";
import { CompareFlyLayer } from "./compare-fly-layer";
import type { ProductDto } from "../../../../api";
import { useWishlistStore } from "../../domain/stores/useWishlistStore";
import { useSimilarProducts } from "../../hooks/useSimilarProducts";
import { ShimmerLoader } from "../../../../components/common/shimmer";
import { getProductDetailsUrl } from "../../utils/get-product-details-url";
import { getWishlistVariantId } from "../../utils/card-props";

const DEFAULT_MAX_COMPARE = 2;

interface SimilarProductsSectionProps {
  referenceProduct?: ProductDto | null;
  /**
   * Variant id of the product currently being viewed. When provided it is
   * prepended to the "Compare now" URL so the compare page loads the active
   * product alongside the user-selected ones.
   */
  activeProductId?: number;
  /** Maximum number of similar products that can be compared at once. */
  maxCompare?: number;
}

export function SimilarProductsSection({
  referenceProduct,
  activeProductId,
  maxCompare = DEFAULT_MAX_COMPARE,
}: SimilarProductsSectionProps) {
  const navigate = useNavigate();
  const location = useLocation();
  // Held as products, not ids filtered from `similarProducts`, so a product
  // added through the tray's catalog search survives too.
  const [selectedProducts, setSelectedProducts] = useState<ProductDto[]>([]);
  const wishlistVariantIds = useWishlistStore((s) => s.wishlistVariantIds);
  const toggle = useWishlistStore((s) => s.toggle);
  const referenceProductId = referenceProduct?.id ?? activeProductId ?? null;

  const similarQuery = useMemo(
    () => (referenceProductId == null ? undefined : { productId: referenceProductId, page: 1, pageSize: 4 }),
    [referenceProductId],
  );

  const { data, isLoading } = useSimilarProducts(similarQuery);
  const similarProducts = data?.data ?? [];

  const toggleCompare = (product: ProductDto) => {
    setSelectedProducts((prev) => {
      if (prev.some((p) => p.id === product.id)) {
        return prev.filter((p) => p.id !== product.id);
      }
      return prev.length < maxCompare ? [...prev, product] : prev;
    });
  };

  const removeFromCompare = (id: number) =>
    setSelectedProducts((prev) => prev.filter((p) => p.id !== id));

  const addToCompare = (product: ProductDto) =>
    setSelectedProducts((prev) =>
      prev.length >= maxCompare || prev.some((p) => p.id === product.id)
        ? prev
        : [...prev, product],
    );

  useEffect(() => {
    setSelectedProducts([]);
  }, [referenceProductId]);

  const selectedIds = useMemo(
    () => new Set(selectedProducts.map((p) => p.id)),
    [selectedProducts],
  );
  const compareDisabled = selectedProducts.length >= maxCompare;
  const productName = referenceProduct?.displayName ?? referenceProduct?.name;

  const handleCompare = () => {
    const ids = [
      ...(referenceProductId != null ? [referenceProductId] : []),
      ...selectedProducts.map((p) => p.id),
    ];
    if (ids.length < 2) return;
    // Extend whatever trail brought us to this product-details page (if any)
    // with the current product, so the compare page shows a full chain.
    const upstream = (
      location.state as { breadcrumbTrail?: BreadcrumbItem[] } | null
    )?.breadcrumbTrail ?? [{ label: "Catalog", path: AppRoutes.PRODUCTS }];
    const trail: BreadcrumbItem[] = [
      ...upstream,
      {
        label: productName ?? "Product",
        path: `${location.pathname}${location.search}`,
      },
    ];
    navigate(`${AppRoutes.PRODUCTS_COMPARE}?ids=${ids.join(",")}`, {
      state: { breadcrumbTrail: trail },
    });
  };

  const visibleProducts = similarProducts.slice(0, 4);

  // Determine if results are "truly similar" (same category/brand) or fallback recommendations.
  // The backend always returns something now, so empty only means the API failed.
  const isFallback = visibleProducts.length > 0 && referenceProduct?.category != null
    && visibleProducts.every((p) => {
      const refCat = typeof referenceProduct.category === 'object' && referenceProduct.category
        ? (referenceProduct.category as { id?: number }).id
        : undefined;
      const pCat = typeof p.category === 'object' && p.category
        ? (p.category as { id?: number }).id
        : undefined;
      return refCat != null && pCat != null && refCat !== pCat;
    });

  const sectionTitle = isFallback ? "You might also like" : "Compare with similar products";
  const sectionSubtitle = isFallback
    ? "We couldn't find exact matches, but these products might interest you"
    : `Select up to ${maxCompare} products to compare side-by-side${productName ? ` with ${productName}` : ""}`;

  return (
    <div className="flex flex-col gap-8 mt-8">
      <div className="flex flex-col gap-1">
        <Heading variant="h3" tone="dark">
          {sectionTitle}
        </Heading>
        <Text variant="bodySm" tone="muted">
          {sectionSubtitle}
        </Text>
      </div>

      {isLoading ? (
        <ShimmerLoader message="Finding similar products…" lines={6} className="max-w-2xl" />
      ) : visibleProducts.length > 0 ? (
        <div className="grid grid-cols-4 gap-20">
          {visibleProducts.map((product) => {
            const wishlistVariantId = getWishlistVariantId(product);
            return (
              <ProductCard
                key={product.id}
                product={product}
                discountLabel={product.standardPrice && product.listPrice && product.standardPrice > product.listPrice
                  ? `${Math.round(((product.standardPrice - product.listPrice) / product.standardPrice) * 100)}% off`
                  : undefined}
                originalPrice={product.standardPrice ?? undefined}
                stockLabel={product.active ? "Available" : "Inactive"}
                compareMode
                compareDisabled={compareDisabled}
                selected={selectedIds.has(product.id)}
                onToggleSelect={() => toggleCompare(product)}
                onViewDetails={() =>
                  navigate(
                    getProductDetailsUrl(
                      product.id,
                      product.variantId ?? null,
                      AppRoutes.PRODUCT_DETAILS,
                    ),
                  )
                }
                isInWishlist={wishlistVariantId != null && wishlistVariantIds.includes(wishlistVariantId)}
                onToggleWishlist={
                  wishlistVariantId != null ? () => void toggle(wishlistVariantId) : undefined
                }
              />
            );
          })}
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed border-borderDefault bg-bgLight px-6 py-10">
          <Text variant="bodySm" tone="muted">
            No products available to compare at this time.
          </Text>
        </div>
      )}

      <ProductsCompareBanner
        selectedProducts={selectedProducts}
        maxCompare={maxCompare}
        minCompare={referenceProductId != null ? 1 : 2}
        onRemove={removeFromCompare}
        onAddProduct={addToCompare}
        pinnedProductIds={referenceProductId != null ? [referenceProductId] : undefined}
        onCompare={handleCompare}
        onClear={() => setSelectedProducts([])}
      />

      <CompareFlyLayer />
    </div>
  );
}
